/**
 * SyncJobCard Component
 *
 * Displays the status and controls for a single sync source (Fathom or HelpScout).
 * Shows progress, metrics, timestamps, and a start button.
 */

import { useState, useEffect } from 'react';
import {
  RefreshCw,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  ExternalLink,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import type { SyncJob, SyncSource, SyncStatus } from '@/types/sync';

interface SyncJobCardProps {
  sync: SyncJob;
  onStartSync: (source: SyncSource) => Promise<void>;
}

const SOURCE_DISPLAY_NAMES: Record<SyncSource, string> = {
  fathom: 'Fathom',
  helpscout: 'HelpScout',
  basecamp: 'Basecamp',
};

const SOURCE_DESCRIPTIONS: Record<SyncSource, string> = {
  fathom: 'Meeting transcripts from Fathom',
  helpscout: 'Customer conversations from HelpScout',
  basecamp: 'Message board posts from Basecamp',
};

function getStatusBadgeVariant(
  status: SyncStatus,
): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'processing':
      return 'default';
    case 'completed':
      return 'secondary';
    case 'failed':
      return 'destructive';
    default:
      return 'outline';
  }
}

function getStatusIcon(status: SyncStatus) {
  switch (status) {
    case 'processing':
      return <RefreshCw className="h-3 w-3 animate-spin" />;
    case 'completed':
      return <CheckCircle className="h-3 w-3" />;
    case 'failed':
      return <XCircle className="h-3 w-3" />;
    default:
      return <Clock className="h-3 w-3" />;
  }
}

function formatTimestamp(timestamp: string | null): string {
  if (!timestamp) return 'Never';
  const date = new Date(timestamp);
  return date.toLocaleString();
}

function formatRelativeTime(timestamp: string | null): string {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} min ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
}

/**
 * Format error message for display.
 * The backend provides user-friendly messages directly.
 */
function formatErrorMessage(errorMessage: string | null): string | null {
  return errorMessage;
}

/**
 * Calculate time-based progress percentage
 */
function calculateTimeProgress(
  startedAt: string | null,
  timeoutSeconds: number,
): number {
  if (!startedAt) return 0;

  const startTime = new Date(startedAt).getTime();
  const now = Date.now();
  const elapsedMs = now - startTime;
  const timeoutMs = timeoutSeconds * 1000;

  // Cap at 95% to indicate we're still running
  return Math.min(95, Math.round((elapsedMs / timeoutMs) * 100));
}

/**
 * Format elapsed time as "Xm Ys"
 */
function formatElapsedTime(startedAt: string | null): string {
  if (!startedAt) return '';

  const startTime = new Date(startedAt).getTime();
  const now = Date.now();
  const elapsedMs = now - startTime;

  const seconds = Math.floor(elapsedMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;

  if (minutes > 0) {
    return `${minutes}m ${remainingSeconds}s`;
  }
  return `${seconds}s`;
}

export function SyncJobCard({ sync, onStartSync }: SyncJobCardProps) {
  const [isStarting, setIsStarting] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);
  const [, setTick] = useState(0);

  const isProcessing = sync.status === 'processing';

  // Update the component every second when processing to show time progress
  useEffect(() => {
    if (!isProcessing) return;

    const interval = setInterval(() => {
      setTick((t) => t + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [isProcessing]);

  // Calculate time-based progress when processing
  const timeProgress = isProcessing
    ? calculateTimeProgress(sync.startedAt, sync.timeoutSeconds || 300)
    : 0;

  const handleStartSync = async () => {
    setIsStarting(true);
    setStartError(null);
    try {
      await onStartSync(sync.source);
    } catch (err) {
      setStartError(
        err instanceof Error ? err.message : 'Failed to start sync',
      );
    } finally {
      setIsStarting(false);
    }
  };

  const friendlyErrorMessage = formatErrorMessage(sync.errorMessage);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle>{SOURCE_DISPLAY_NAMES[sync.source]}</CardTitle>
            <CardDescription>
              {SOURCE_DESCRIPTIONS[sync.source]}
            </CardDescription>
          </div>
          <Badge variant={getStatusBadgeVariant(sync.status)}>
            {getStatusIcon(sync.status)}
            <span className="ml-1 capitalize">{sync.status}</span>
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Time-based progress bar when processing */}
        {isProcessing && (
          <div className="space-y-2">
            <Progress value={timeProgress} />
            <div className="flex justify-between text-sm text-muted-foreground">
              <span>{sync.itemsProcessed} items processed</span>
              <span>{formatElapsedTime(sync.startedAt)}</span>
            </div>
          </div>
        )}

        {/* Metrics */}
        <div className="grid grid-cols-3 gap-4 text-sm">
          <div>
            <div className="text-muted-foreground">Synced</div>
            <div className="font-medium">{sync.itemsSynced}</div>
          </div>
          <div>
            <div className="text-muted-foreground">Skipped</div>
            <div className="font-medium">{sync.itemsSkipped}</div>
          </div>
          <div>
            <div className="text-muted-foreground">Failed</div>
            <div className="font-medium">{sync.itemsFailed}</div>
          </div>
        </div>

        {/* Timestamps */}
        <div className="space-y-1 text-sm text-muted-foreground">
          {isProcessing && sync.startedAt && (
            <div>Started: {formatRelativeTime(sync.startedAt)}</div>
          )}
          {!isProcessing && sync.lastSuccessAt && (
            <div>Last success: {formatRelativeTime(sync.lastSuccessAt)}</div>
          )}
          {!isProcessing && sync.completedAt && (
            <div title={formatTimestamp(sync.completedAt)}>
              Completed: {formatRelativeTime(sync.completedAt)}
            </div>
          )}
        </div>

        {/* Error message with CloudWatch link */}
        {friendlyErrorMessage && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="flex flex-col gap-2">
              <span>{friendlyErrorMessage}</span>
              {sync.logsUrl && (
                <a
                  href={sync.logsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs underline hover:no-underline"
                >
                  View logs in CloudWatch
                  <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </AlertDescription>
          </Alert>
        )}

        {/* Start error */}
        {startError && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{startError}</AlertDescription>
          </Alert>
        )}

        {/* Start button */}
        <Button
          onClick={handleStartSync}
          disabled={isProcessing || isStarting}
          className="w-full"
        >
          {isStarting ? (
            <>
              <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
              Starting...
            </>
          ) : isProcessing ? (
            <>
              <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
              Syncing...
            </>
          ) : (
            <>
              <RefreshCw className="mr-2 h-4 w-4" />
              Start Sync
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
}
